import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

// Cutoff date for grandfathering - clients before this date bypass onboarding
export const ONBOARDING_CUTOFF_DATE = "2025-12-18";

interface OnboardingStatus {
  needsOnboarding: boolean;
  isGrandfathered: boolean;
  isAdmin: boolean;
  clientId: string | null;
  onboardingStatus: string | null;
  loading: boolean;
}

export function useOnboardingStatus() {
  const [status, setStatus] = useState<OnboardingStatus>({
    needsOnboarding: false,
    isGrandfathered: false,
    isAdmin: false,
    clientId: null,
    onboardingStatus: null,
    loading: true,
  });

  useEffect(() => {
    checkOnboardingStatus();
  }, []);

  const checkOnboardingStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setStatus(prev => ({ ...prev, loading: false }));
        return;
      }

      // Check if user is admin
      const { data: adminRole } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();

      if (adminRole) {
        // Admins never need onboarding
        setStatus({
          needsOnboarding: false,
          isGrandfathered: true,
          isAdmin: true,
          clientId: null,
          onboardingStatus: null,
          loading: false,
        });
        return;
      }

      // Get client record
      const { data: clientData } = await supabase
        .from("clients")
        .select("id, created_at, approved, onboarding_status, archived")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!clientData) {
        setStatus(prev => ({ ...prev, loading: false }));
        return;
      }

      // Check if archived
      if (clientData.archived) {
        setStatus(prev => ({ ...prev, loading: false }));
        return;
      }

      // Check grandfathering conditions:
      // 1. Client created before cutoff date
      // 2. Client already approved
      // 3. Client has onboarding_status = 'grandfathered' or 'completed'
      const clientCreatedAt = new Date(clientData.created_at);
      const cutoffDate = new Date(ONBOARDING_CUTOFF_DATE);
      
      const isGrandfathered = 
        clientCreatedAt < cutoffDate ||
        clientData.approved === true ||
        clientData.onboarding_status === "grandfathered" ||
        clientData.onboarding_status === "completed";

      const needsOnboarding = 
        !isGrandfathered && 
        (clientData.onboarding_status === "not_started" || 
         clientData.onboarding_status === "in_progress" ||
         !clientData.onboarding_status);

      setStatus({
        needsOnboarding,
        isGrandfathered,
        isAdmin: false,
        clientId: clientData.id,
        onboardingStatus: clientData.onboarding_status,
        loading: false,
      });
    } catch (error) {
      console.error("Error checking onboarding status:", error);
      setStatus(prev => ({ ...prev, loading: false }));
    }
  };

  return { ...status, refresh: checkOnboardingStatus };
}
